/**
 * Moving a session on the projection — one dispatch for the pointer and the keyboard, and the
 * sentences a screen reader hears.
 *
 * ── Both endings are decided in one place ────────────────────────────────────────────────────
 * A monitor rather than a handler per cell, for the reason `useWeekMove` records: a release that
 * lands on NOTHING is a cancel, and only a monitor sees it — a target's own `onDrop` never fires
 * for that case. So a cancelled pointer drag takes exactly the path a cancelled keyboard lift
 * does: the pending aim clears, nothing is written, the trainer is told.
 *
 * ── The refusal is WORDED for the keyboard and SILENT for the pointer ───────────────────────
 * View ADR §11 lets a pointer drag refuse by never entering the target: no indicator, no cursor
 * change, and a live region firing on every cell the pointer crosses would be a torrent nobody can
 * read mid-gesture. The arrows are the opposite — a trainer who aimed and pressed Space asked the
 * question explicitly — so the keyboard path announces `dropVerdict`'s reason. One rule, two
 * deliveries; the menu path this app does NOT need is the popover, which reaches the same override
 * through a date field.
 *
 * ── Everything the sentence needs rides on the drag payload ──────────────────────────────────
 * Reading the chip's title back out of the projection at drop time would name whatever now stands
 * on that coordinate, which after a re-projection is not necessarily the chip that was lifted.
 */

import { useEffect, useRef, useState } from 'react';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useTranslation } from 'react-i18next';

import { useAnnouncer } from '~/shared/lib/useAnnouncer';

import { dropRefusalMessage } from '../model/assignRefusalCopy';
import type { ProjectionChip } from '../model/projectionGrid';
import type { DropVerdict } from '../model/projectionDrop';

/** The payload a chip attaches to its drag. `kind` is what the monitor filters on. */
export const CHIP_DRAG_KIND = 'assign-occurrence';

/** A keyboard lift in progress: which chip, where it is aimed, and whether that aim is allowed. */
export interface ProjectionLift {
  readonly key: string;
  readonly iso: string;
  /** `null` on the pickup itself, where the chip is still aimed at its own day. */
  readonly verdict: DropVerdict | null;
}

export interface ProjectionMove {
  /** The live region's text. Always mounted; empty when there is nothing to say. */
  readonly announcement: string;
  /**
   * The lift in progress, or `null`.
   *
   * One object rather than three parallel `useState`s: the three were always written together and
   * always cleared together, and a cell asking "is the lift aimed at me, and was it refused?" had
   * to read all three and cross-check them.
   */
  readonly lift: ProjectionLift | null;
  /** Whether a chip may land on a day at all — the pointer adapter's own gate. */
  readonly canDrop: (chipKey: string, iso: string) => boolean;
  /** Space on a chip. */
  readonly start: (chip: ProjectionChip) => void;
  /** An arrow while lifted. */
  readonly aim: (chip: ProjectionChip, iso: string) => void;
  /** Space again. Commits through the same `moveOccurrence` the pointer drop uses. */
  readonly drop: (chip: ProjectionChip, iso: string) => void;
  /** Escape, a blur, or a release over nothing. */
  readonly cancel: (chip: ProjectionChip | null) => void;
}

export function useProjectionMove(options: {
  readonly move: (chip: ProjectionChip, iso: string) => DropVerdict;
  readonly verdictFor: (chip: ProjectionChip, iso: string) => DropVerdict;
  /** Every chip on the grid, by key — how the monitor resolves what was dropped. */
  readonly chips: ReadonlyMap<string, ProjectionChip>;
}): ProjectionMove {
  const { t } = useTranslation();
  const { message, announce } = useAnnouncer();
  const [lift, setLift] = useState<ProjectionLift | null>(null);

  const commit = (chip: ProjectionChip, iso: string) => {
    const verdict = options.move(chip, iso);
    if (verdict.ok) {
      announce(t('web:assign.drag.dropped', { title: chip.title, date: iso }));
    } else {
      announce(dropRefusalMessage(verdict.refusal, t));
    }
    setLift(null);
  };

  // The latest `commit` and chip table, so the monitor below can be bound ONCE. Rebinding it every
  // render would tear it down mid-gesture, and a monitor unbound between `onDragStart` and `onDrop`
  // never sees the drop at all. Written in an EFFECT — `react-hooks/refs` — which is safe because
  // the only reader is a drop callback, long after effects have run.
  const latest = useRef({ commit, chips: options.chips });
  useEffect(() => {
    latest.current.commit = commit;
    latest.current.chips = options.chips;
  });

  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) => source.data.kind === CHIP_DRAG_KIND,
        onDrop: ({ source, location }) => {
          const iso = location.current.dropTargets[0]?.data.iso;
          const key = source.data.chipKey;
          if (typeof iso !== 'string' || typeof key !== 'string') return;
          const chip = latest.current.chips.get(key);
          if (chip === undefined) return;
          latest.current.commit(chip, iso);
        },
      }),
    [],
  );

  return {
    announcement: message,
    lift,
    canDrop: (chipKey, iso) => {
      const chip = options.chips.get(chipKey);
      return chip !== undefined && options.verdictFor(chip, iso).ok;
    },
    start: (chip) => {
      setLift({ key: chip.key, iso: chip.iso, verdict: null });
      announce(t('web:assign.drag.lifted', { title: chip.title, date: chip.iso }));
    },
    aim: (chip, iso) => {
      const verdict = options.verdictFor(chip, iso);
      setLift({ key: chip.key, iso, verdict });
      // The aim says the DATE, and the reason only when there is one. A refusal read out on every
      // arrow press through a legal run of days would bury the date under it.
      announce(
        verdict.ok
          ? t('web:assign.drag.aim', { date: iso })
          : t('web:assign.drag.aimRefused', {
              date: iso,
              reason: dropRefusalMessage(verdict.refusal, t),
            }),
      );
    },
    drop: commit,
    cancel: (chip) => {
      if (lift === null) return;
      setLift(null);
      announce(t('web:assign.drag.cancelled', { title: chip?.title ?? '' }));
    },
  };
}
